import type {World} from '../shared/types';
import {mergeWorld} from './worldState';

export interface MetadataContext {identity:number;writerId:string;worldEpoch:number;moderationRevision:number}
export interface MetadataReadStamp extends MetadataContext {foregroundVersion:number;mutationVersion:number}
export function sameMetadataContext(a:MetadataContext|null,b:MetadataContext|null):boolean {
 return !!a&&!!b&&a.identity===b.identity&&a.writerId===b.writerId&&a.worldEpoch===b.worldEpoch&&a.moderationRevision===b.moderationRevision;
}
export function metadataReadIsCurrent(start:MetadataReadStamp,current:MetadataReadStamp|null):boolean {
 return sameMetadataContext(start,current)&&start.foregroundVersion===current?.foregroundVersion&&start.mutationVersion===current.mutationVersion;
}

/** Descriptors for all opened blocks arrive with the foreground world. Only
 * blocks whose selected metadata passed that context's publication fence count
 * as loaded. Viewport changes reprioritize work without forgetting useful reads. */
export class WorldMetadataProgress {
 private context:MetadataContext|null=null;
 private loaded=new Set<number>();
 clear(){this.context=null;this.loaded.clear();}
 record(context:MetadataContext,blocks:readonly number[]){
  if(!sameMetadataContext(this.context,context)){this.loaded.clear();this.context={...context};}
  for(const id of blocks)if(Number.isSafeInteger(id)&&id>=0)this.loaded.add(id);
 }
 has(context:MetadataContext,blocks:readonly number[]){return sameMetadataContext(this.context,context)&&blocks.every(id=>this.loaded.has(id));}
 next(context:MetadataContext,world:Pick<World,'blocks'|'frontier'>,priority:readonly number[]):number[]{
  if(!sameMetadataContext(this.context,context)||!Number.isSafeInteger(world.frontier)||world.frontier<0)return [];
  const opened=new Set(world.blocks.map(block=>block.id).filter(id=>Number.isSafeInteger(id)&&id>=0&&id<=world.frontier));
  return [...new Set([...priority,world.frontier,...[...opened].sort((a,b)=>a-b)])].filter(id=>opened.has(id)&&!this.loaded.has(id)).slice(0,6);
 }
}

/** A background batch can add metadata, never certify another generation or
 * visibility revision. Merge against the latest state to retain live commits. */
export function mergeWorldMetadata(current:World,snapshot:World):World|null {
 if((current.worldEpoch??0)!==(snapshot.worldEpoch??0)||(current.moderationRevision??0)!==(snapshot.moderationRevision??0))return null;
 return mergeWorld(current,snapshot);
}
